import React, { Fragment, useContext, useEffect, useState } from "react";
import moment from "moment";
import InputField from "../../CommonComponents/InputField";
import InputDropdown from "../../CommonComponents/InputDropdown";
import Textarea from "../../CommonComponents/Textarea";
import DragAndDropUpload from "../../CommonComponents/DragAndDropUpload";
import Button from "../../Button";
import AlertContext from "../../../context/AlertContext";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import constants from "../../../constants";
import { getDataFromStorage, MANAGEMENTROLES } from "../../../util";
import Toast from "../../CommonComponents/Toast";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";

export const supportedFormat = ".zip, .jpeg, .jpg, .png",
  maxChar = 80,
  supportMimeTypes = [
    "application/zip",
    "application/x-zip-compressed",
    "image/png",
    "image/jpg",
    "image/apng",
    "image/jpeg",
  ],
  fields = ["title", "type", "bid", "description"];
export const validate = (name, value) => {
  switch (name) {
    case "title":
      if (!value || value.trim() === "") return "Title is required";
      break;
    case "type":
      if (!value || !Number.isInteger(Number(value)))
        return "Please select the type";
      break;
    case "description":
      if (value && value.length > 1000) return "The maximum character is 1000.";
      break;
    case "bid":
      if (!value || !Number.isInteger(Number(value)))
        return "Please select the batch";
      break;
    default: {
      return "";
    }
  }
};

const IssueForm = ({
  initialDetails,
  setShowForm,
  isEdit,
  details,
  setDetails,
  selectedBatch,
  setSelectedBatch,
  selectedType,
  setSelectedType,
  setIsSuccess,
}) => {
  const { setIsLoaded, setShowNotify, isAdmin, isLoaded } =
    useContext(AlertContext);
  const axios = useAxiosPrivate();
  const { onUploadS3Object } = useS3FileManagement();

  const [titleCharCount, setTitleCharCount] = useState(0);
  const [types, setTypes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [errors, setErrors] = useState({});
  const [isRemoved, setIsRemoved] = useState(false);

  const isDisabled = !!MANAGEMENTROLES.includes(getDataFromStorage("role"));
  const companyId = isAdmin
    ? getDataFromStorage("companyId")
    : parseInt(localStorage.getItem("companyId"));

  const [toastOpen, setToastOpen] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  useEffect(() => {
    const fetchBatchesAndTypes = async () => {
      try {
        setIsLoaded(true);
        const response = await axios.get(
          `node/learner/batch?company_id=${companyId}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const response2 = await axios.get(
          `node/learner/issue/type?request_type=0`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (response2.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
          const data = response2.data.data ?? [];
          setTypes(
            data.map((item, index) => ({
              ...item,
              value: item.id,
              label: item.name,
            })),
          );
          setErrors({});
        } else {
          setShowNotify({
            show: true,
            title: "Error!",
            msg:
              response.data.msg ??
              "Failed to fetch issue types. Please try again later or contact gradious team.",
          });
        }
        if (response.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
          const data = response.data.data ?? [];
          setBatches(
            data.map((item, index) => ({
              ...item,
              value: item.id,
              label: item.name,
            })),
          );
        } else {
          setShowNotify({
            show: true,
            title: "Error!",
            msg:
              response.data.msg ??
              "Failed to fetch batches. Please try again later or contact gradious team.",
          });
        }
      } catch (error) {
        setShowNotify({
          show: true,
          title: error.message.includes("403") ? "Warning!" : "Error!",
          msg: error.message.includes("403")
            ? "You have been logged-out due to inactivity. Login again."
            : (error.message ??
              "Something went wrong. Please try again later or contact gradious team."),
          isUnAuth: error.message.includes("403"),
        });
      } finally {
        setIsLoaded(false);
      }
    };
    fetchBatchesAndTypes();
  }, [axios, setIsLoaded, setShowNotify, companyId]);

  const onChange = (name, value = "") => {
    if (name === "title") {
      setTitleCharCount(value.length);
      if (value.length === maxChar) return;
    }
    setErrors({ ...errors, [name]: validate(name, value) });
    setDetails({ ...details, [name]: value });
  };

  const onSelectIssueType = (value) => {
    setSelectedType(value);
    let typeOptions = structuredClone(types);
    let type = typeOptions.find((batch) => batch.value === value);
    setErrors({ ...errors, type: validate("type", value) });
    setDetails({
      ...details,
      type: type.value,
      typeName: type.label,
    });
  };
  const onSelectBatch = (value) => {
    setSelectedBatch(value);
    let batchOptions = structuredClone(batches);
    let batch = batchOptions.find((batch) => batch.value === value);
    setErrors({ ...errors, bid: validate("bid", value) });
    setDetails({ ...details, bid: batch.value, bname: batch.label });
  };
  console.log("selectedBatch", selectedBatch);
  console.log("batches", batches);

  const onDragOver = (event) => event.preventDefault();

  const onDrop = (event) => {
    const files = Array.from(event.dataTransfer.files);
    uploadFiles(files);
  };

  const uploadFiles = (files = []) => {
    if (files.length > 1) {
      setErrors({
        ...errors,
        attachment:
          "Multiple files are not supported in this format. Please upload a single ZIP file.",
      });
      return;
    }
    const isUploadableFormat = files.every((file) =>
      supportMimeTypes.includes(file.type),
    );
    if (!isUploadableFormat) {
      setErrors({
        ...errors,
        attachment: `Unsupported file type: ${
          files[0].type.split("/")[1]
        }. Allowed formats are: ${supportedFormat}`,
      });
      return;
    }
    setDetails({ ...details, attachment: files[0] });
    setErrors({ ...errors, attachment: "" });
  };

  const onUpload = (event) => {
    const files = Array.from(event.target.files);
    uploadFiles(files);
    event.target.value = "";
  };

  const onRemoveFile = () => {
    if (isEdit) setIsRemoved(true);
    setDetails({ ...details, attachment: null });
  };

  const onSumbitOrUpdate = async (event) => {
    // console.log("event:",event);
    setIsLoaded(true);
    event.preventDefault();
    let validationErrors = {};
    fields.forEach((name) => {
      const error = validate(name, details[name]);
      if (error && error.length > 0) {
        validationErrors[name] = error;
      }
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    let batchDetails = batches.filter((batch) => batch.id === details?.bid);
    // console.log("batchdetails:", batchDetails);

    if (batchDetails[0]?.type === constants.BATCH_TYPE.TESTING) {
      setToastOpen(true);
      setDetails({});
      setSelectedBatch(null);
      setSelectedType(null);
      setIsLoaded(false);
      return;
    }
    try {
      let attachment = details.attachment,
        path;
      if (attachment) {
        let imageName = attachment.name;
        path =
          !isEdit || (isEdit && isRemoved)
            ? `issues/${imageName?.substring(
                0,
                imageName.lastIndexOf("."),
              )}_${moment().toISOString()}${imageName?.substring(
                imageName.lastIndexOf("."),
              )}`
            : `issues/${imageName}`;
        if (!isEdit || (isEdit && isRemoved))
          await onUploadS3Object(
            path,
            attachment,
            imageName?.split(".").pop().toLowerCase(),
            "HOST",
            "PUT",
          );
        // details.attachment = path;
        details.attachmentSize = `${Math.floor(
          details.attachment.size / 125000,
        )}MB`;
        details.attachmentName = details.attachment.name;
      }
      const method = isEdit ? "put" : "post";
      const url = isEdit
        ? `node/learner/issue/${details.id}`
        : "node/learner/issue";
      if (isEdit) delete details.id;
      const response = await axios({
        method,
        url,
        data: { ...details, attachment: path },
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.data.resultCode === constants.RESULT_STATUS.SUCCESS)
        setIsSuccess(true);
      else {
        setShowNotify({
          show: true,
          title: "Error!",
          msg:
            response.data.msg ??
            `Failed to ${
              isEdit ? "update" : "add"
            } issue. Please try again later or contact gradious team.`,
        });
      }
    } catch (error) {
      setShowNotify({
        show: true,
        title: error.message.includes("403") ? "Warning!" : "Error!",
        msg: error.message.includes("403")
          ? "You have been logged-out due to inactivity. Login again."
          : (error.message ??
            "Something went wrong. Please try again later or contact gradious team."),
        isUnAuth: error.message.includes("403"),
      });
    } finally {
      setIsLoaded(false);
      onCancel();
    }
  };

  const onCancel = (event) => {
    event?.preventDefault();
    setShowForm(false);
    setDetails(initialDetails);
    setIsSuccess(false);
    setSelectedBatch(null);
    setSelectedType(null);
    setErrors({});
  };

  return (
    <Fragment>
      {/* {!isSuccess ? ( */}
      <form className="issueFormContainer">
        <div className="issueFormFields">
          <Toast
            open={toastOpen}
            onClose={() => setToastOpen(false)}
            icon={
              <svg
                width="56"
                height="56"
                fill="none"
                viewBox="0 0 56 56"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="4"
                  y="4"
                  width="48"
                  height="48"
                  rx="24"
                  fill={"#FEF0C7"}
                />
                <rect
                  x="4"
                  y="4"
                  rx="24"
                  width="48"
                  height="48"
                  stroke-width="8"
                  stroke="#FFFBFA"
                />
                <path
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M27.9998 24.0012V28.0012M27.9998 32.0012H28.0098M26.2898 18.8612L17.8198 33.0012C17.6451 33.3036 17.5527 33.6465 17.5518 33.9957C17.5508 34.3449 17.6413 34.6883 17.8142 34.9917C17.9871 35.2951 18.2365 35.5479 18.5375 35.725C18.8385 35.9021 19.1806 35.9973 19.5298 36.0012H36.4698C36.819 35.9973 37.1611 35.9021 37.4621 35.725C37.7631 35.5479 38.0124 35.2951 38.1854 34.9917C38.3583 34.6883 38.4488 34.3449 38.4478 33.9957C38.4468 33.6465 38.3544 33.3036 38.1798 33.0012L29.7098 18.8612C29.5315 18.5673 29.2805 18.3243 28.981 18.1557C28.6814 17.987 28.3435 17.8984 27.9998 17.8984C27.656 17.8984 27.3181 17.987 27.0186 18.1557C26.7191 18.3243 26.468 18.5673 26.2898 18.8612Z"
                  stroke={"#D92D20"}
                />
              </svg>
            }
            closeIcon={
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="#667085"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            }
            message={"You can't raise an issue for the selected batch"}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          />
          <InputField
            name="title"
            label="Title"
            placeholder="What’s the title of the issue?"
            hintText={
              errors["title"] && errors["title"].length
                ? errors.title
                : `${titleCharCount}/80 characters`
            }
            value={details.title}
            onChange={(event) =>
              onChange(event.target.name, event.target.value)
            }
            disabled={isDisabled}
            destructive={errors["title"] && errors["title"].length}
          />
          <div className="issueFormSelectFieldSec">
            <InputDropdown
              type="default"
              label="Type"
              placeholder="Select the type of issue"
              options={types}
              onChange={onSelectIssueType}
              value={selectedType}
              disabled={isDisabled}
              hintText={errors["type"]}
              destructive={!!errors["type"]}
            />
            <InputDropdown
              label="Batch"
              placeholder="Select the batch you face this issue in"
              options={batches ?? []}
              searchable
              type="default"
              className="issueFormBatchSelect"
              onChange={onSelectBatch}
              value={selectedBatch ?? ""}
              disabled={isDisabled}
              hintText={errors["bid"]}
              destructive={!!errors["bid"]}
            />
          </div>
          <Textarea
            placeholder="Explain your issue in detail here"
            name="description"
            value={details.description}
            onChange={(event) =>
              onChange(event.target.name, event.target.value)
            }
            disabled={isDisabled}
            hintText={errors["description"]}
            destructive={!!errors["description"]}
          />
          {details && (details?.attachment || details.status !== 2) && (
            <DragAndDropUpload
              onDragOver={onDragOver}
              onDrop={onDrop}
              file={details.attachment}
              onRemoveFile={onRemoveFile}
              disabled={isDisabled}
              isDeletable={
                !MANAGEMENTROLES.includes(getDataFromStorage("role"))
              }
              isDownloadable={
                !!MANAGEMENTROLES.includes(getDataFromStorage("role"))
              }
              isSuccessfullyUpload={
                !MANAGEMENTROLES.includes(getDataFromStorage("role"))
              }
              button={
                <Button
                  size="sm"
                  hierarchy={{ type: "secondaryGrey", buttonText: "Upload" }}
                  icon={{
                    type: "leading",
                    icon: (
                      <svg
                        width="20"
                        height="21"
                        viewBox="0 0 20 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.5 13V16.3333C17.5 16.7754 17.3244 17.1993 17.0118 17.5118C16.6993 17.8244 16.2754 18 15.8333 18H4.16667C3.72464 18 3.30072 17.8244 2.98816 17.5118C2.67559 17.1993 2.5 16.7754 2.5 16.3333V13M14.1667 7.16667L10 3M10 3L5.83333 7.16667M10 3V13"
                          stroke="#344054"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ),
                  }}
                />
              }
              text="Drop your screenshot of the issue here"
              supportFormat={supportedFormat}
              onUpload={onUpload}
              hintText={errors["attachment"]}
              destructive={!!errors["attachment"]}
            />
          )}
        </div>
        {!MANAGEMENTROLES.includes(getDataFromStorage("role")) &&
          details &&
          details?.status !== 2 && (
            <div className="issueFormBtnSec">
              <Button
                size="lg"
                hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
                onClick={onCancel}
              />
              <Button
                size="lg"
                hierarchy={{
                  type: "primary",
                  buttonText: isEdit ? "Update" : "Submit",
                }}
                onClick={onSumbitOrUpdate}
                disable={isLoaded}
              />
            </div>
          )}
      </form>
      {/* ) : (
        <div className="issueSuccessContainer">
          <div className="issueSuccessIcon">
            <svg
              width="144"
              height="150"
              viewBox="0 0 144 150"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M132 69.2858V75.0358C131.993 88.5134 127.803 101.627 120.056 112.422C112.309 123.217 101.42 131.114 89.0121 134.935C76.6046 138.757 63.3435 138.298 51.2068 133.627C39.0701 128.956 28.7079 120.324 21.6658 109.017C14.6236 97.711 11.2787 84.3362 12.1301 70.8877C12.9814 57.4393 17.9832 44.6378 26.3897 34.3925C34.7961 24.1471 46.1567 17.0069 58.7771 14.0367C71.3975 11.0665 84.6016 12.4254 96.42 17.9108M132 25L72 87.5625L54 68.8125"
                stroke="#12B76A"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="issueSuccessText">
              New Issue successfully {isEdit ? "updated" : "submitted"}
            </div>
          </div>
          <Button
            size="lg"
            hierarchy={{ type: "primary", buttonText: "Ok" }}
            onClick={onCancel}
          />
        </div>
      )} */}
    </Fragment>
  );
};

export default IssueForm;
